/**
 * Formats de formation (colonne `formations.format`).
 *
 * Nouvelle nomenclature, déployée sur les tenants Apteed et Onedys uniquement :
 *
 *   « Mixte »          -> « Présentiel PI »  +  « Classe virtuelle PI »
 *   « Non présentiel » -> « E-learning FC »  +  « E-learning PI »  +  « EPP »
 *
 * Les autres tenants (hp, igeya, iefsante…) gardent l'ancienne liste : leurs
 * données n'ont pas été reprises, « Mixte » / « Non présentiel » y sont encore
 * les valeurs stockées.
 *
 * Le tenant est déduit du sous-domaine, comme dans AccountPopover et formationApi.
 */

/** Tenants passés à la nouvelle nomenclature. */
const NEW_FORMAT_TENANTS = ['apteed', 'onedys'];

export const currentTenant = () => {
  if (typeof window === 'undefined') return '';
  return (window.location.hostname || '').split('.')[0].toLowerCase();
};

export const usesNewFormats = () => NEW_FORMAT_TENANTS.includes(currentTenant());

/** Liste historique, conservée pour les tenants non repris. */
export const LEGACY_FORMAT_OPTIONS = [
  'Mixte',
  'Non présentiel',
  'Présentiel',
  'Classe virtuelle',
];

/** Nouvelle liste : « Mixte » et « Non présentiel » éclatés. */
export const NEW_FORMAT_OPTIONS = [
  'Présentiel',
  'Classe virtuelle',
  'Présentiel PI',
  'Classe virtuelle PI',
  'E-learning FC',
  'E-learning PI',
  'EPP',
];

/** Options du sélecteur « Format » pour le tenant courant. */
export const formatOptions = () => (usesNewFormats() ? NEW_FORMAT_OPTIONS : LEGACY_FORMAT_OPTIONS);

/**
 * Toutes les valeurs susceptibles d'être stockées, anciennes et nouvelles
 * confondues (formats hybrides historiques inclus). Sert aux listes de filtres,
 * qui doivent continuer à reconnaître l'existant.
 */
export const ALL_FORMAT_VALUES = [
  'Présentiel',
  'Classe virtuelle',
  'Présentiel PI',
  'Classe virtuelle PI',
  'E-learning FC',
  'E-learning PI',
  'EPP',
  'Mixte',
  'Non présentiel',
  'E-Learning',
  'E-learning/Présentiel',
  'Pas disponible',
];

/**
 * Formats issus de « Mixte ». Ils héritent de son comportement métier :
 * une inscription y est « terminée » sur la progression, pas sur la présence.
 */
export const PI_FORMATS = ['Présentiel PI', 'Classe virtuelle PI'];

/** Formats issus de « Non présentiel ». */
export const ELEARNING_FORMATS = ['E-learning FC', 'E-learning PI', 'EPP'];

/**
 * Formats dont l'état d'une inscription se juge à la présence et non à la
 * seule progression — « Mixte » et ses deux remplaçants, plus « Présentiel ».
 * (Reproduit la condition historique `format === 'Mixte' || 'Présentiel'`.)
 */
export const PRESENCE_DRIVEN_FORMATS = ['Mixte', 'Présentiel', ...PI_FORMATS];

export const isPresenceDriven = (format) => PRESENCE_DRIVEN_FORMATS.includes(format);

/**
 * Formats qui réunissent physiquement les apprenants dans une salle : les
 * seuls à donner lieu à une feuille d'émargement papier. « Classe virtuelle »
 * et « Classe virtuelle PI » en sont exclues, comme les formats à distance.
 */
export const PRESENTIEL_FORMATS = ['Présentiel', 'Présentiel PI'];

/** « Présentiel PI » -> « presentiel pi » (tolère casse, accent et espaces). */
const normalizeFormat = (format) =>
  (format || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[éèêë]/g, 'e')
    .replace(/\s+/g, ' ');

export const isPresentiel = (format) =>
  PRESENTIEL_FORMATS.map(normalizeFormat).includes(normalizeFormat(format));

/**
 * Une formation entièrement en salle ne laisse aucune trace de connexion : ni Zoom,
 * ni TalentLMS, ni Evalgo n'ont quoi que ce soit à fournir. Le bouton « Télécharger
 * les logs » y est donc masqué (la preuve de présence, c'est l'émargement).
 * Les formats PI gardent le bouton : leur partie en ligne a bien des logs.
 */
export const hasLearnerLogs = (format) => normalizeFormat(format) !== 'presentiel';

/** Formats qui réunissent les apprenants en visioconférence. */
export const CLASSE_VIRTUELLE_FORMATS = ['Classe virtuelle', 'Classe virtuelle PI'];

export const isClasseVirtuelle = (format) =>
  CLASSE_VIRTUELLE_FORMATS.map(normalizeFormat).includes(normalizeFormat(format));

/**
 * Formats dont la session réunit formateur et apprenants à un moment donné —
 * en salle ou en visio. Ce sont les seuls concernés par le questionnaire de
 * satisfaction du formateur, qui n'existe qu'en deux modèles.
 */
export const hasFormateurSurvey = (format) =>
  isPresentiel(format) || isClasseVirtuelle(format);

/**
 * Valeurs de format encore présentes en base mais qu'on ne veut plus proposer
 * dans les filtres : elles restent stockées sur d'anciennes formations, mais
 * ne doivent plus apparaître comme un choix.
 */
export const HIDDEN_FORMAT_VALUES = ['E-learning/Présentiel', 'Pas disponible', 'E-Learning'];

/** Retire les formats masqués d'une liste de choix (filtres, listes déroulantes). */
export const withoutHiddenFormats = (formats) =>
  (Array.isArray(formats) ? formats : []).filter(
    (f) => !HIDDEN_FORMAT_VALUES.map(normalizeFormat).includes(normalizeFormat(f))
  );
